/**
 * Main product — variant sync, price, EMI, gallery, qty, inventory, hot stock
 */
(function () {
  'use strict';

  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function formatMoney(cents, formatTemplate) {
    var abs = Math.abs(Number(cents));
    if (formatTemplate == null || formatTemplate === '') {
      var r = abs / 100;
      return abs % 100 === 0 ? String(Math.round(r)) : r.toFixed(2);
    }
    var amount = (abs / 100).toFixed(2);
    var parts = amount.split('.');
    var amountNoDecimals = parts[0];
    var withComma = amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var wholeRupees = abs % 100 === 0;
    var displayAmount = wholeRupees ? amountNoDecimals : amount;
    var commaSep = wholeRupees ? withComma : withComma + '.' + parts[1];
    var html = String(formatTemplate)
      .replace(/\{\{\s*amount\s*\}\}/g, displayAmount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, commaSep);
    return html;
  }

  function moneyWithoutTrailingZeros(cents, formatTemplate) {
    return formatMoney(cents, formatTemplate);
  }

  function parseVariants(root) {
    var id = root.getAttribute('data-section-id');
    var el = document.getElementById('ProductVariantJson-' + id);
    if (!el) return [];
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return [];
    }
  }

  function findVariant(variants, select) {
    if (!select) return variants[0] || null;
    var vid = parseInt(select.value, 10);
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].id === vid) return variants[i];
    }
    return variants[0] || null;
  }

  function buildPriceHtml(variant, moneyWithCurrency) {
    if (!variant) return '';
    var current = formatMoney(variant.price, moneyWithCurrency);
    var html =
      '<div class="price-block price--large dz-price">' +
      '<span class="price-block__current">' +
      current +
      '</span>';
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      var compare = formatMoney(variant.compare_at_price, moneyWithCurrency);
      var save = variant.compare_at_price - variant.price;
      var pct = Math.round((save * 100) / variant.compare_at_price);
      html +=
        '<span class="price-block__compare"><s>' +
        compare +
        '</s></span>' +
        '<span class="price-block__off">(' +
        pct +
        '% off)</span>';
    }
    html +=
      '<span class="ej-tax">Inclusive of all taxes</span>' +
      '</div>';
    return html;
  }

  function getGalleryViewport(root) {
    return root.querySelector('[data-gallery-viewport]');
  }

  function getGallerySlides(viewport) {
    return viewport ? viewport.querySelectorAll('.ej-media__slide[data-gallery-src]') : [];
  }

  /** Which slide is most visible inside the scrollport (works when slide width ≠ clientWidth). */
  function getGalleryActiveIndex(viewport) {
    var slides = getGallerySlides(viewport);
    if (!slides.length) return 0;
    if (viewport.clientWidth <= 0) return 0;
    var v = viewport.getBoundingClientRect();
    var bestIdx = 0;
    var bestOverlap = -1;
    for (var i = 0; i < slides.length; i++) {
      var r = slides[i].getBoundingClientRect();
      var overlap = Math.min(r.right, v.right) - Math.max(r.left, v.left);
      if (overlap > bestOverlap) {
        bestOverlap = overlap;
        bestIdx = i;
      }
    }
    return bestIdx;
  }

  function applyGalleryActiveIndex(root, viewport, idx) {
    var slides = getGallerySlides(viewport);
    if (!slides.length) return;
    if (idx < 0) idx = 0;
    if (idx >= slides.length) idx = slides.length - 1;
    var slide = slides[idx];
    var src = slide ? slide.getAttribute('data-gallery-src') : '';
    var gallery = root.querySelector('[data-product-gallery]');
    if (!gallery) return;
    if (src) {
      gallery.querySelectorAll('.pmg__thumb').forEach(function (btn) {
        btn.classList.toggle('is-active', btn.getAttribute('data-image-src') === src);
      });
    }
    gallery.querySelectorAll('[data-gallery-dots] .ej-gallery-dot').forEach(function (dot, i) {
      dot.classList.toggle('is-active', i === idx);
      dot.setAttribute('aria-selected', i === idx ? 'true' : 'false');
    });
  }

  function syncThumbsToViewport(viewport, root) {
    if (!viewport) return;
    var slides = getGallerySlides(viewport);
    if (!slides.length || viewport.clientWidth <= 0) return;
    applyGalleryActiveIndex(root, viewport, getGalleryActiveIndex(viewport));
  }

  /** Keeps dots/thumbs in sync when scroll events are flaky (iOS momentum, scroll-snap). */
  function setupGalleryIntersectionSync(viewport, root) {
    var slides = getGallerySlides(viewport);
    if (!slides.length || typeof IntersectionObserver === 'undefined') return null;
    var ratios = new Array(slides.length);
    var t;
    for (t = 0; t < slides.length; t++) ratios[t] = 0;
    var thresholds = [];
    for (t = 0; t <= 20; t++) thresholds.push(t / 20);
    var io = new IntersectionObserver(
      function (entries) {
        var e;
        for (e = 0; e < entries.length; e++) {
          var entry = entries[e];
          var idx = Array.prototype.indexOf.call(slides, entry.target);
          if (idx >= 0) ratios[idx] = entry.intersectionRatio;
        }
        var bestIdx = 0;
        var bestR = -1;
        var j;
        for (j = 0; j < ratios.length; j++) {
          if (ratios[j] > bestR) {
            bestR = ratios[j];
            bestIdx = j;
          }
        }
        applyGalleryActiveIndex(root, viewport, bestIdx);
      },
      { root: viewport, rootMargin: '0px', threshold: thresholds }
    );
    for (t = 0; t < slides.length; t++) io.observe(slides[t]);
    return io;
  }

  function updateGalleryMain(root, src, alt, options) {
    if (!src) return;
    options = options || {};
    var id = root.getAttribute('data-section-id');
    var viewport = getGalleryViewport(root);
    var gallery = root.querySelector('[data-product-gallery]');
    var animate = options.animate !== false && !prefersReducedMotion();

    root._ejGalleryTransitionGen = (root._ejGalleryTransitionGen || 0) + 1;
    var gen = root._ejGalleryTransitionGen;

    function clearViewportTransition() {
      if (root._ejGalleryTransitionGen !== gen) return;
      if (viewport) viewport.classList.remove('ej-gallery-viewport--transitioning');
    }

    function scheduleEndAfterScroll() {
      var done = function () {
        if (root._ejGalleryTransitionGen !== gen) return;
        clearViewportTransition();
      };
      if (viewport && typeof viewport.addEventListener === 'function') {
        viewport.addEventListener('scrollend', done, { once: true, passive: true });
      }
      window.setTimeout(done, 520);
    }

    if (viewport) {
      var slides = viewport.querySelectorAll('.ej-media__slide[data-gallery-src]');
      var target = null;
      var i;
      for (i = 0; i < slides.length; i++) {
        if (slides[i].getAttribute('data-gallery-src') === src) {
          target = slides[i];
          break;
        }
      }
      if (target) {
        var gi = Array.prototype.indexOf.call(slides, target);
        if (gi >= 0) {
          applyGalleryActiveIndex(root, viewport, gi);
        }
        var curIdx = getGalleryActiveIndex(viewport);
        var needsScroll = gi !== curIdx;
        if (animate && viewport && needsScroll) {
          viewport.classList.add('ej-gallery-viewport--transitioning');
          window.requestAnimationFrame(function () {
            window.requestAnimationFrame(function () {
              if (root._ejGalleryTransitionGen !== gen) return;
              target.scrollIntoView({
                inline: 'start',
                block: 'nearest',
                behavior: 'smooth',
              });
              scheduleEndAfterScroll();
            });
          });
        } else {
          if (needsScroll) {
            target.scrollIntoView({
              inline: 'start',
              block: 'nearest',
              behavior: animate ? 'smooth' : 'auto',
            });
          }
          clearViewportTransition();
        }
        var imgEl = target.querySelector('img');
        if (imgEl && alt) imgEl.alt = alt;
      } else {
        var img = document.getElementById('ProductImage-' + id);
        if (img) {
          if (animate) {
            img.classList.add('ej-gallery-image--fading');
            window.setTimeout(function () {
              if (root._ejGalleryTransitionGen !== gen) return;
              img.src = src;
              if (alt) img.alt = alt;
              var clearFade = function () {
                if (root._ejGalleryTransitionGen !== gen) return;
                img.classList.remove('ej-gallery-image--fading');
              };
              img.addEventListener('load', clearFade, { once: true });
              img.addEventListener('error', clearFade, { once: true });
              window.setTimeout(clearFade, 400);
            }, 180);
          } else {
            img.src = src;
            if (alt) img.alt = alt;
          }
        }
      }
    } else {
      var img2 = document.getElementById('ProductImage-' + id);
      if (img2) {
        img2.src = src;
        if (alt) img2.alt = alt;
      }
    }

    if (gallery) {
      gallery.querySelectorAll('.pmg__thumb').forEach(function (btn) {
        btn.classList.toggle('is-active', btn.getAttribute('data-image-src') === src);
      });
      if (viewport) {
        var slides2 = getGallerySlides(viewport);
        var si;
        for (si = 0; si < slides2.length; si++) {
          if (slides2[si].getAttribute('data-gallery-src') === src) {
            applyGalleryActiveIndex(root, viewport, si);
            break;
          }
        }
      }
    }
  }

  /** Close full-screen layers that block the PDP (lens drawer, cart drawer, header menu). */
  function closeBlockingOverlays() {
    var cd = document.querySelector('cart-drawer');
    if (cd) {
      cd.classList.remove('active', 'animate');
      if (typeof cd.close === 'function') {
        try {
          cd.close();
        } catch (e) {}
      }
    }
    var hd = document.getElementById('header-drawer');
    if (hd) {
      hd.classList.remove('is-open');
      hd.setAttribute('aria-hidden', 'true');
      hd.querySelectorAll('.has-children.is-open').forEach(function (el) {
        el.classList.remove('is-open');
      });
      hd.querySelectorAll('[data-drawer-toggle]').forEach(function (btn) {
        btn.setAttribute('aria-expanded', 'false');
      });
    }
    var d = document.getElementById('lens-drawer');
    if (d) {
      d.setAttribute('aria-hidden', 'true');
      d.classList.remove('active', 'animate');
      var sheet = d.querySelector('.la-sheet');
      if (sheet) sheet.style.transform = 'translateY(100%)';
    }
    document.body.style.overflow = '';
    document.body.classList.remove('overflow-hidden');
  }

  /** When >5 colour swatches, row is horizontal scroll; keep the current swatch visible. */
  function scrollModelSwatchRowIntoView(root) {
    var wrap = root.querySelector('.ej-model-swatches--scroll');
    if (!wrap) return;
    var row = wrap.querySelector('.ej-model-swatches__row');
    var cur = row && row.querySelector('.ej-model-swatches__link.is-current');
    if (!row || !cur || typeof cur.scrollIntoView !== 'function') return;
    window.requestAnimationFrame(function () {
      try {
        cur.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'auto' });
      } catch (e) {
        cur.scrollIntoView();
      }
    });
  }

  /**
   * GoEye-style instant colour switch: Section Rendering API (no full reload).
   * @see https://shopify.dev/docs/api/ajax/section-rendering
   */
  function initModelSwatchInstantSwitch(root) {
    var wrap = root.querySelector('[data-model-color-swatches]');
    if (!wrap) return;

    /**
     * Section Rendering API keys must be Liquid section.id values (e.g. template--123__main),
     * not section filenames. Filenames like "main-product" return null and break the swap.
     */
    var mainSectionId = root.getAttribute('data-section-id');
    if (!mainSectionId) return;

    var relRoot = document.querySelector('[data-ej-section-file="related-products"]');
    var relSectionId = relRoot && relRoot.getAttribute('data-section-id');
    var sectionsParam = relSectionId ? mainSectionId + ',' + relSectionId : mainSectionId;

    var isSwitching = false;

    function productHandleFromUrl(href) {
      try {
        var u = new URL(href, window.location.origin);
        var parts = u.pathname.split('/').filter(Boolean);
        var pi = parts.indexOf('products');
        return pi >= 0 && parts[pi + 1] ? parts[pi + 1] : '';
      } catch (e) {
        return '';
      }
    }

    wrap.querySelectorAll('a.ej-model-swatches__link[href]').forEach(function (a) {
      a.addEventListener(
        'click',
        function (e) {
        if (e.defaultPrevented) return;
        if (e.button !== 0) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

        var handle =
          a.getAttribute('data-ej-product-handle') || productHandleFromUrl(a.href);
        if (!handle) return;

        var livePdp = document.querySelector('[data-main-product]');
        var currentHandle =
          (livePdp && livePdp.getAttribute('data-product-handle')) ||
          root.getAttribute('data-product-handle') ||
          '';
        if (a.classList.contains('is-current') || handle === currentHandle) return;
        if (isSwitching) return;

        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') {
          e.stopImmediatePropagation();
        }

        closeBlockingOverlays();

        isSwitching = true;
        a.classList.add('ej-swatch-loading');

        var sr = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        if (!sr.endsWith('/')) sr += '/';
        var fetchUrl =
          sr +
          'products/' +
          encodeURIComponent(handle) +
          '?sections=' +
          encodeURIComponent(sectionsParam);

        fetch(fetchUrl)
          .then(function (r) {
            if (!r.ok) throw new Error('bad response');
            return r.json();
          })
          .then(function (data) {
            var mainHtml = data[mainSectionId];
            if (mainHtml == null || mainHtml === '') {
              throw new Error('no main section markup');
            }

            var parser = new DOMParser();
            var wrapped =
              '<div class="ej-parse-wrap">' + mainHtml + '</div>';
            var doc = parser.parseFromString(wrapped, 'text/html');
            var container = doc.body.firstElementChild;
            var newMain =
              (container &&
                container.querySelector('[data-ej-section-file="main-product"]')) ||
              (container && container.querySelector('[data-main-product]'));
            if (!newMain) throw new Error('could not parse main-product');

            var curMain =
              document.querySelector('[data-ej-section-file="main-product"]') ||
              document.querySelector('[data-main-product]');
            if (!curMain) throw new Error('missing current PDP root');

            curMain.replaceWith(document.importNode(newMain, true));

            closeBlockingOverlays();
            window.requestAnimationFrame(function () {
              closeBlockingOverlays();
            });

            var newRoot = document.querySelector('[data-main-product]');
            if (!newRoot) throw new Error('missing new PDP root');

            var titleEl = newRoot.querySelector('.ej-title');
            if (titleEl && titleEl.textContent) {
              document.title = titleEl.textContent.trim();
            }

            var relHtml =
              relSectionId && data[relSectionId] != null && data[relSectionId] !== ''
                ? data[relSectionId]
                : null;
            if (relHtml) {
              var doc2 = parser.parseFromString(
                '<div class="ej-parse-wrap">' + relHtml + '</div>',
                'text/html'
              );
              var c2 = doc2.body.firstElementChild;
              var newRel =
                c2 &&
                (c2.querySelector('[data-ej-section-file="related-products"]') ||
                  c2.querySelector('product-recommendations'));
              var curRel = document.querySelector(
                '[data-ej-section-file="related-products"]'
              );
              if (newRel && curRel) {
                curRel.replaceWith(document.importNode(newRel, true));
              }
            }

            window.history.replaceState(
              { ejProduct: handle },
              '',
              sr + 'products/' + encodeURIComponent(handle)
            );

            a.classList.remove('ej-swatch-loading');
            closeBlockingOverlays();

            isSwitching = false;
            init(newRoot);

            if (typeof window.__ejLensSelectorInit === 'function') {
              window.__ejLensSelectorInit();
            }

            if (!prefersReducedMotion()) {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              window.scrollTo(0, 0);
            }
          })
          .catch(function () {
            isSwitching = false;
            a.classList.remove('ej-swatch-loading');
            window.location.href = a.href;
          });
        },
        true
      );
    });
  }

  function updateHotStockMount(mount, variant, maxStock) {
    if (!mount) return;
    var qty = variant.inventory_quantity || 0;
    var show = qty > 0 && qty <= maxStock;
    if (!show) {
      mount.innerHTML = '';
      return;
    }
    mount.innerHTML =
      '<div class="hot-stock">' +
      '<p class="hot-stock__msg">Please hurry! Only ' +
      qty +
      ' left in stock</p>' +
      '</div>';
  }

  function updateInventoryRow(root, variant) {
    var row = root.querySelector('[data-inventory-row]');
    if (!row) return;
    var im = variant.inventory_management;
    var qty = variant.inventory_quantity;
    var textEl = row.querySelector('[data-inventory-text]');
    if (im === 'shopify' && qty > 0) {
      row.hidden = false;
      if (textEl) textEl.textContent = 'In stock';
    } else if (im === 'shopify' && qty <= 0 && variant.inventory_policy === 'continue') {
      row.hidden = false;
      if (textEl) textEl.textContent = 'In stock';
    } else {
      row.hidden = true;
    }
  }

  function applyVariant(root, variant, variants, moneyWithCurrency) {
    var sid = root.getAttribute('data-section-id');
    var moneyWC = moneyWithCurrency;

    var priceHtml = buildPriceHtml(variant, moneyWC);
    var priceEl = document.getElementById('price-' + sid);
    if (priceEl) priceEl.innerHTML = priceHtml;

    // Update inline prices (next to Add to Cart button)
    if (variant) {
      var currentPrice = formatMoney(variant.price, moneyWC);
      var hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
      var comparePrice = hasCompare ? formatMoney(variant.compare_at_price, moneyWC) : '';
      var savePct = hasCompare ? Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price) : 0;

      // Update main inline price
      root.querySelectorAll('[data-inline-price-current]').forEach(function (el) {
        el.innerHTML = currentPrice;
      });
      root.querySelectorAll('[data-inline-price-compare]').forEach(function (el) {
        el.innerHTML = comparePrice;
        var row = el.closest('.ej-inline-price__row');
        if (row) row.style.display = hasCompare ? 'flex' : 'none';
      });
      root.querySelectorAll('[data-inline-price-off]').forEach(function (el) {
        el.textContent = hasCompare ? '(' + savePct + '% off)' : '';
      });

      // Update sticky bar inline price
      root.querySelectorAll('[data-sticky-price-current]').forEach(function (el) {
        el.innerHTML = currentPrice;
      });
      root.querySelectorAll('[data-sticky-price-compare]').forEach(function (el) {
        el.innerHTML = comparePrice;
        var row = el.closest('.ej-inline-price__row');
        if (row) row.style.display = hasCompare ? 'flex' : 'none';
      });
      root.querySelectorAll('[data-sticky-price-off]').forEach(function (el) {
        el.textContent = hasCompare ? '(' + savePct + '% off)' : '';
      });
    }

    var emiEl = document.getElementById('emi-highlight-' + sid);
    if (emiEl && variant) {
      var wrap = emiEl.closest('[data-emi-tenure]');
      var tenure = wrap
        ? parseInt(wrap.getAttribute('data-emi-tenure'), 10) || 3
        : 3;
      var perMonth = Math.ceil(variant.price / tenure);
      emiEl.textContent = moneyWithoutTrailingZeros(perMonth, moneyWC).replace(/<[^>]+>/g, '');
    }

    var inst = document.getElementById('installment-variant-' + sid);
    if (inst && variant) inst.value = variant.id;

    if (variant && variant.featured_media) {
      updateGalleryMain(root, variant.featured_media, '');
    }

    var maxHot = parseInt(
      (root.querySelector('[data-hot-stock-wrap]') || {}).getAttribute('data-hot-stock-max') ||
        '20',
      10
    );
    updateHotStockMount(root.querySelector('[data-hot-stock-wrap]'), variant, maxHot);

    updateInventoryRow(root, variant);

    var addBtns = root.querySelectorAll('[data-add-to-cart]');
    var addTexts = root.querySelectorAll('.buy-buttons__cart-text');
    var disabled = !variant || !variant.available;
    addBtns.forEach(function (addBtn) {
      addBtn.disabled = disabled;
    });
    root.querySelectorAll('[data-buy-now]').forEach(function (btn) {
      btn.disabled = disabled;
    });
    root.querySelectorAll('[data-gokwik-buy]').forEach(function (gkBtn) {
      if (disabled) {
        gkBtn.setAttribute('disabled', 'disabled');
        gkBtn.classList.add('disabled');
      } else {
        gkBtn.removeAttribute('disabled');
        gkBtn.classList.remove('disabled');
      }
    });
    root.querySelectorAll('[data-open-lens-selector]').forEach(function (btn) {
      btn.disabled = disabled;
    });
    addTexts.forEach(function (addText) {
      addText.textContent =
        variant && variant.available ? 'Add To Cart' : 'Sold out';
    });

    var vSel = root.querySelector('[data-variant-select]');
    if (vSel && variant && typeof window !== 'undefined') {
      window.mainProductVariantId = String(vSel.value);
    }
  }

  function initSwcCarousel(root) {
    var wrap = root.querySelector('[data-swc-carousel]');
    if (!wrap) return;
    var viewport = wrap.querySelector('[data-swc-viewport]');
    var track = wrap.querySelector('[data-swc-track]');
    if (!viewport || !track) return;
    var n = parseInt(wrap.getAttribute('data-swc-count') || '0', 10);
    if (n < 3) return;

    var delay = parseInt(wrap.getAttribute('data-swc-delay') || '4000', 10);
    var cards = track.querySelectorAll('.ej-swc-card');
    var index = 0;
    var step = 0;
    var timer = null;

    function measure(resetIndex) {
      var w = viewport.clientWidth;
      if (w <= 0) return;
      var gap = 8;
      var slideW = (w - 2 * gap) / 3;
      step = slideW + gap;
      for (var i = 0; i < cards.length; i++) {
        cards[i].style.width = slideW + 'px';
        cards[i].style.flexBasis = slideW + 'px';
      }
      if (resetIndex) {
        index = 0;
        track.classList.add('ej-swc-track--no-trans');
        track.style.transform = 'translate3d(0,0,0)';
        void track.offsetWidth;
        track.classList.remove('ej-swc-track--no-trans');
      } else {
        track.style.transform = 'translate3d(-' + index * step + 'px,0,0)';
      }
    }

    function goNext() {
      index += 1;
      track.classList.remove('ej-swc-track--no-trans');
      track.style.transform = 'translate3d(-' + index * step + 'px,0,0)';
    }

    track.addEventListener('transitionend', function (e) {
      if (e.target !== track || e.propertyName !== 'transform') return;
      if (index === n) {
        track.classList.add('ej-swc-track--no-trans');
        index = 0;
        track.style.transform = 'translate3d(0,0,0)';
        void track.offsetWidth;
        track.classList.remove('ej-swc-track--no-trans');
      }
    });

    function startTimer() {
      if (prefersReducedMotion()) return;
      if (timer) clearInterval(timer);
      timer = setInterval(goNext, delay);
    }

    function onResize() {
      measure(true);
    }

    measure(false);
    startTimer();

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(onResize, 150);
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        if (timer) clearInterval(timer);
        timer = null;
      } else {
        startTimer();
      }
    });
  }

  function init(root) {
    initSwcCarousel(root);
    initModelSwatchInstantSwitch(root);
    scrollModelSwatchRowIntoView(root);
    var variants = parseVariants(root);
    var moneyWC =
      root.getAttribute('data-money-with-currency') ||
      root.getAttribute('data-money-format') ||
      '{{amount}}';

    var gallery = root.querySelector('[data-product-gallery]');
    var viewport = getGalleryViewport(root);
    if (gallery) {
      gallery.querySelectorAll('.pmg__thumb').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var src = btn.getAttribute('data-image-src');
          var alt = btn.getAttribute('data-image-alt') || '';
          updateGalleryMain(root, src, alt);
        });
      });
      gallery.querySelectorAll('[data-gallery-dots] .ej-gallery-dot').forEach(function (dot, index) {
        dot.addEventListener('click', function () {
          if (!viewport) return;
          var slides = getGallerySlides(viewport);
          var target = slides[index];
          if (target) {
            applyGalleryActiveIndex(root, viewport, index);
            viewport.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
          }
        });
      });
    }
    if (viewport) {
      var galleryScrollRaf = null;
      function onGalleryScroll() {
        if (galleryScrollRaf != null) return;
        galleryScrollRaf = requestAnimationFrame(function () {
          galleryScrollRaf = null;
          syncThumbsToViewport(viewport, root);
        });
      }
      viewport.addEventListener('scroll', onGalleryScroll, { passive: true });
      viewport.addEventListener('scrollend', onGalleryScroll, { passive: true });
      setupGalleryIntersectionSync(viewport, root);
      syncThumbsToViewport(viewport, root);
    }

    root.querySelectorAll('[data-qty-minus]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var input = root.querySelector('.quantity-eyejack__input');
        if (!input) return;
        var n = parseInt(input.value, 10) || 1;
        input.value = Math.max(1, n - 1);
      });
    });
    root.querySelectorAll('[data-qty-plus]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var input = root.querySelector('.quantity-eyejack__input');
        if (!input) return;
        var n = parseInt(input.value, 10) || 1;
        input.value = n + 1;
      });
    });

    var qtyInputEl = root.querySelector('.quantity-eyejack__input');
    if (qtyInputEl) {
      qtyInputEl.addEventListener(
        'wheel',
        function (e) {
          e.preventDefault();
        },
        { passive: false }
      );
    }

    if (!variants.length) return;

    var select = root.querySelector('[data-variant-select]');

    function onVariantChange() {
      var v = findVariant(variants, select);
      applyVariant(root, v, variants, moneyWC);
    }

    if (select) {
      select.addEventListener('change', onVariantChange);
    }

    onVariantChange();

    root.querySelectorAll('[data-open-lens-selector]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (typeof window.openLensAdvisorDrawer !== 'function' && typeof window.__ejLensSelectorInit === 'function') {
          window.__ejLensSelectorInit();
        }
        if (typeof window.openLensAdvisorDrawer === 'function') {
          window.openLensAdvisorDrawer(btn);
          return;
        }
        var tid = btn.getAttribute('data-lens-target');
        var el = tid ? document.getElementById(tid) : null;
        if (!el) el = root.querySelector('.ej-frame-box');
        if (!el) el = root.querySelector('.ej-detail');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    root.querySelectorAll('[data-buy-now]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        if (btn.disabled) return;
        e.preventDefault();
        var form = root.querySelector('.ej-product-form');
        if (!form) return;
        var addBtn = form.querySelector('button[name="add"][data-add-to-cart]');
        if (!addBtn && form.id) {
          addBtn = root.querySelector(
            'button[name="add"][data-add-to-cart][form="' + form.id + '"]'
          );
        }
        if (!addBtn) {
          addBtn = root.querySelector('button[name="add"][data-add-to-cart]');
        }
        if (!addBtn || addBtn.disabled) return;
        if (typeof window.__ejHandleProductBuyNow === 'function') {
          window.__ejHandleProductBuyNow(form, btn);
        } else if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(addBtn);
        } else {
          addBtn.click();
        }
      });
    });
  }

  /** Legacy hook from other themes / onclick handlers */
  window.openLensSelector = function (e) {
    if (e && e.preventDefault) e.preventDefault();
    var first = document.querySelector('[data-open-lens-selector]');
    if (typeof window.openLensAdvisorDrawer === 'function') {
      window.openLensAdvisorDrawer(first || null);
      return;
    }
    if (first) {
      first.click();
      return;
    }
    var anchor = document.querySelector('[id^="lens-selector-"]');
    if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  document.querySelectorAll('[data-main-product]').forEach(init);
})();
